import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle, Loader2, Clock } from "lucide-react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/ui/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import { Helmet } from "react-helmet-async";

const SALES_TYPES = ["Test Drive", "Consultation", "Trade-In Appraisal"];
const SERVICE_TYPES = ["Routine Maintenance", "Repair", "Inspection", "Winterization"];

const EDGE_URL = `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/appointment-book`;

function formatTime(t: string) {
  const [h, m] = t.split(":").map(Number) as [number, number];
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function ScheduleAppointment() {
  const [department, setDepartment] = useState<"sales" | "service" | "">("");
  const [appointmentType, setAppointmentType] = useState("");
  const [date, setDate] = useState<Date>();
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsClosed, setSlotsClosed] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset downstream when department changes
  useEffect(() => {
    setAppointmentType("");
    setDate(undefined);
    setSlots([]);
    setSelectedSlot("");
  }, [department]);

  // Reset slots when date changes
  useEffect(() => {
    setSelectedSlot("");
    if (!department || !date) {
      setSlots([]);
      return;
    }
    const fetchSlots = async () => {
      setSlotsLoading(true);
      setSlotsClosed(false);
      try {
        const res = await fetch(EDGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "get-availability",
            department,
            date: format(date, "yyyy-MM-dd"),
          }),
        });
        const data = await res.json();
        setSlots(data.slots || []);
        setSlotsClosed(!!data.closed || !!data.blackout);
      } catch {
        toast({ title: "Error loading availability", variant: "destructive" });
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [department, date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: "Name and email are required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          customer_name: form.name.trim(),
          customer_email: form.email.trim(),
          customer_phone: form.phone.trim() || null,
          department,
          appointment_type: appointmentType,
          date: format(date!, "yyyy-MM-dd"),
          start_time: selectedSlot,
          notes: form.notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        toast({ title: "Error", description: data.error || "Could not book appointment.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Network error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const types = department === "sales" ? SALES_TYPES : department === "service" ? SERVICE_TYPES : [];
  const canPickDate = !!department && !!appointmentType;
  const canPickSlot = canPickDate && !!date;
  const canSubmit = canPickSlot && !!selectedSlot;

  if (success) {
    return (
      <Layout>
        <Helmet><title>Appointment Confirmed | Rex & Sons RVs</title></Helmet>
        <PageHero title="Appointment Requested" backgroundImage={STATIC_ASSETS.heroService1920} backgroundImageMobile={STATIC_ASSETS.heroService640} />
        <section className="container py-16">
          <div className="mx-auto max-w-lg text-center">
            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-primary" />
            <h2 className="font-heading text-2xl font-bold text-foreground">Thank You!</h2>
            <p className="mt-2 text-muted-foreground">
              Your appointment request has been submitted. We'll confirm it shortly.
              You'll receive an email at <strong>{form.email}</strong> with the details.
            </p>
            <Button className="mt-6" onClick={() => window.location.reload()}>
              Schedule Another
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Schedule an Appointment | Rex & Sons RVs</title>
      </Helmet>
      <PageHero title="Schedule an Appointment" backgroundImage={STATIC_ASSETS.heroService1920} backgroundImageMobile={STATIC_ASSETS.heroService640} />

      <section className="container py-12">
        <div className="mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Step 1: Department */}
            <Card>
              <CardHeader><CardTitle className="text-lg">1. Select Department</CardTitle></CardHeader>
              <CardContent className="flex gap-3">
                {(["sales", "service"] as const).map((d) => (
                  <Button
                    key={d}
                    type="button"
                    variant={department === d ? "default" : "outline"}
                    className="flex-1 capitalize"
                    onClick={() => setDepartment(d)}
                  >
                    {d}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Step 2: Type */}
            {department && (
              <Card>
                <CardHeader><CardTitle className="text-lg">2. Appointment Type</CardTitle></CardHeader>
                <CardContent>
                  <Select value={appointmentType} onValueChange={setAppointmentType}>
                    <SelectTrigger><SelectValue placeholder="Choose type..." /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Date */}
            {canPickDate && (
              <Card>
                <CardHeader><CardTitle className="text-lg">3. Choose Date</CardTitle></CardHeader>
                <CardContent>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().toDateString())}
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Time Slot */}
            {canPickSlot && (
              <Card>
                <CardHeader><CardTitle className="text-lg">4. Choose Time</CardTitle></CardHeader>
                <CardContent>
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading available times...
                    </div>
                  ) : slotsClosed ? (
                    <p className="text-muted-foreground">We're closed on this date. Please choose another day.</p>
                  ) : slots.length === 0 ? (
                    <p className="text-muted-foreground">No available slots for this date. Please choose another day.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {slots.map((slot) => (
                        <Button
                          key={slot}
                          type="button"
                          variant={selectedSlot === slot ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedSlot(slot)}
                        >
                          <Clock className="mr-1 h-3 w-3" />
                          {formatTime(slot)}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 5: Customer Info */}
            {canSubmit && (
              <Card>
                <CardHeader><CardTitle className="text-lg">5. Your Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="sa-name">Full Name *</Label>
                      <Input id="sa-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} maxLength={100} />
                    </div>
                    <div>
                      <Label htmlFor="sa-email">Email *</Label>
                      <Input id="sa-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} maxLength={255} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="sa-phone">Phone</Label>
                    <Input id="sa-phone" type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} maxLength={20} />
                  </div>
                  <div>
                    <Label htmlFor="sa-notes">Notes (optional)</Label>
                    <Textarea id="sa-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} maxLength={1000} />
                  </div>
                </CardContent>
              </Card>
            )}

            {canSubmit && (
              <Button type="submit" size="lg" className="w-full font-heading uppercase tracking-wide" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarIcon className="mr-2 h-4 w-4" />}
                {submitting ? "Booking..." : "Request Appointment"}
              </Button>
            )}
          </form>
        </div>
      </section>
    </Layout>
  );
}
